import React from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, CheckCircle2, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Mandala from "@/components/Mandala";

// Local-SEO landing page targeting "aerial yoga teacher training Columbia MD"
// + Maryland-area variants. All CTAs point at /trainings/aerial-yoga which
// is the canonical training index. Uses a distinct H1 with the location
// exact-match so Google ranks it for regional queries where the big
// national brands aren't optimized.
export default function AerialYTTColumbiaMD() {
  return (
    <div className="min-h-screen bg-aviana-paper" data-testid="ytt-columbia-md-page">
      <SEO
        title="Aerial Yoga Teacher Training in Columbia, MD · Yoga Alliance YACEP"
        description="A Yoga Alliance YACEP-approved aerial yoga teacher training in Columbia, Maryland — held at The Pearl. 50 hours of certification with Holly Johnson of Aviana Yoga: sequencing, safety, sensory support, and the business of teaching aerial."
        path="/aerial-yoga-teacher-training-columbia-md"
        keywords="aerial yoga teacher training Columbia MD, aerial yoga certification Maryland, aerial yoga training Columbia Maryland, aerial yoga instructor Maryland, Yoga Alliance YACEP Maryland, Aviana Yoga Columbia, The Pearl aerial yoga"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Course",
            "name": "Aerial Yoga Teacher Training — Columbia, MD",
            "description": "50-hour Yoga Alliance YACEP-approved aerial yoga teacher training held at The Pearl in Columbia, Maryland with Holly Johnson of Aviana Yoga.",
            "provider": { "@id": "https://avianayoga.com/#organization" },
            "instructor": { "@id": "https://avianayoga.com/#holly" },
            "educationalCredentialAwarded": "50-hour Yoga Alliance YACEP Certificate",
            "hasCourseInstance": {
              "@type": "CourseInstance",
              "courseMode": "InPerson",
              "location": {
                "@type": "Place",
                "name": "The Pearl",
                "address": { "@type": "PostalAddress", "addressLocality": "Columbia", "addressRegion": "MD", "addressCountry": "US" }
              }
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://avianayoga.com/" },
              { "@type": "ListItem", "position": 2, "name": "Aerial Yoga Teacher Training", "item": "https://avianayoga.com/trainings/aerial-yoga" },
              { "@type": "ListItem", "position": 3, "name": "Columbia, MD", "item": "https://avianayoga.com/aerial-yoga-teacher-training-columbia-md" }
            ]
          }
        ]}
      />

      <section className="pt-36 pb-16 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden">
        <Mandala size={480} variant="lotus" className="absolute -right-24 -top-16 text-aviana-gold opacity-[0.09] pointer-events-none hidden md:block" />
        <div className="container-aviana relative">
          <p className="overline text-aviana-goldSoft mb-4">Yoga Alliance YACEP · 50 hours · Columbia, Maryland</p>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.98] max-w-4xl">
            Aerial Yoga <em className="italic font-serif gold-text">Teacher Training</em>
            <br />in Columbia, MD.
          </h1>
          <p className="mt-8 max-w-2xl text-[17px] text-aviana-cream/90 leading-[1.75]">
            Aviana Yoga is bringing our 50-hour Yoga Alliance YACEP-approved aerial yoga teacher training to <strong>The Pearl in Columbia, Maryland</strong>. Three full days of immersive certification with Holly Johnson — the founder and lead trainer behind the Aviana methodology.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/trainings/aerial-yoga" className="btn-gold inline-flex items-center gap-2" data-testid="cta-view-training">
              View training details <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="btn-secondary !text-aviana-cream !border-aviana-cream/40 hover:!border-aviana-gold">Ask a question →</Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-aviana-cream/80">
            <span className="inline-flex items-center gap-2"><MapPin size={14} className="text-aviana-gold" /> The Pearl · Columbia, MD</span>
            <span className="inline-flex items-center gap-2"><Calendar size={14} className="text-aviana-gold" /> October 9–11, 2026</span>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-aviana max-w-3xl">
          <p className="overline-plum mb-4">Why Maryland aerial teachers choose Aviana</p>
          <h2 className="font-display text-3xl md:text-4xl text-aviana-ink leading-tight mb-8">
            The most immersive aerial yoga certification serving the Maryland, DC, and Northern Virginia area.
          </h2>
          <div className="prose prose-lg max-w-none text-aviana-inkSoft leading-[1.85]">
            <p>
              Finding <strong>aerial yoga teacher training in Columbia, MD</strong> — or anywhere in Maryland — usually means a long flight or a compromise on quality. Aviana solves that. Holly Johnson brings our full 50-hour YACEP curriculum directly to <em>The Pearl</em>, a bespoke aerial studio in Columbia's Long Reach neighborhood, for a three-day immersion designed to fully certify you to teach aerial yoga.
            </p>
            <p>
              Whether you're a 200-hour RYT ready to add aerial to your offerings, a movement instructor stepping into yoga, or a passionate practitioner ready to teach, this <strong>aerial yoga certification in Maryland</strong> covers everything: 60+ asanas across two full class sequences, rigging safety, spotting and modifications, aromatherapy 101, chakra-based sequencing, the business of aerial yoga, and Aviana's signature goddess-archetype teaching arc.
            </p>
            <h3>What's included in your Columbia, MD training</h3>
            <ul>
              <li><CheckCircle2 size={16} className="inline text-aviana-gold mr-2" />50-hour Yoga Alliance YACEP certification pathway (accepted internationally)</li>
              <li><CheckCircle2 size={16} className="inline text-aviana-gold mr-2" />Three full days in-person at The Pearl, Columbia MD · 10am–7pm</li>
              <li><CheckCircle2 size={16} className="inline text-aviana-gold mr-2" />Five months to complete the online modules (exam, research paper, practice teaching, class audit)</li>
              <li><CheckCircle2 size={16} className="inline text-aviana-gold mr-2" />Lifetime access to Aviana's Sisters of the Silks community</li>
              <li><CheckCircle2 size={16} className="inline text-aviana-gold mr-2" />Ongoing 1:1 mentorship with Holly during and after your training</li>
            </ul>
            <h3>Who this Maryland aerial yoga training is for</h3>
            <p>
              This training is designed for people in the Baltimore–DC corridor who want to become aerial yoga instructors without flying to Chicago, Los Angeles, or Bali to do it. Past cohorts have included:
            </p>
            <ul>
              <li>Currently-certified 200/500-hour yoga teachers expanding into aerial</li>
              <li>Pilates, barre, and dance instructors adding aerial to their studio programming</li>
              <li>Devoted practitioners ready to teach after several years of consistent aerial practice</li>
              <li>Studio owners wanting to bring aerial into their existing offerings</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-20 bg-aviana-cream">
        <div className="container-aviana max-w-3xl text-center">
          <p className="overline-plum mb-3">Reserve your seat</p>
          <h2 className="font-display text-3xl md:text-4xl text-aviana-ink leading-tight mb-4">
            Ready to teach aerial yoga in Maryland?
          </h2>
          <p className="text-aviana-inkSoft text-[15px] leading-[1.7] max-w-xl mx-auto mb-8">
            View the full training details, dates, tuition breakdown, and secure your spot — or reach out with a question first.
          </p>
          <Link to="/trainings/aerial-yoga" className="btn-gold inline-flex items-center gap-2" data-testid="cta-bottom">
            See the full training <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
